import React, { useState, useEffect, useRef, lazy, Suspense } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Send, Music, X, User, CreditCard, Loader2 } from 'lucide-react';
import { useSongRequests } from '../hooks/useSongRequests';
import A2BPaymentForm from '@/components/a2b/A2BPaymentForm';

const PayPalUSProcessor = lazy(() => import('@/components/paypal/PayPalUSProcessor.jsx'));
import { capitalizeWords } from '@/lib/utils';
import { trackSongInteraction, resolveArtistLiveSessionId } from '@/lib/viewerTracking';
import { useCurrency } from '@/contexts/CurrencyContext';
import { formatCurrency, DEFAULT_CURRENCY } from '@/lib/currency';
import { useToast } from '@/components/ui/use-toast';
import { isPayPalCheckoutCountry, isPayPalPaymentProvider } from '@/lib/paypalCheckout';

export const SongCard = ({ 
  song, 
  artistId,
  // stripeAccountId removed - Trust Payments does not require account ID
  sessionId = null,
  isPlayed = false, 
  isLive = false,
  acceptingNewRequests = true, // Whether artist is accepting new requests
  mode = 'artist',
  theme = 'default',
  onRequestSubmitted,
  artistCurrency = null, // ✅ Artist's currency - if provided, use this instead of visitor's currency
  artistCountry = null, // PayPal for US/CA/IE/AU/NZ; A2B for UK (GB)
  /** When true, show price on the "by …" line and shorten the row (e.g. public page Artists tab). */
  subtitlePriceInline = false,
  /** Public live page: Request Song adds to the floating basket instead of opening the modal. */
  basketMode = false,
  inBasket = false,
  onToggleBasket = null,
}) => {
  const usePayPal = isPayPalCheckoutCountry(artistCountry);
  const [modalOpen, setModalOpen] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const viewedTrackedForOpenRef = useRef(false);
  const cartTrackedForOpenRef = useRef(false);
  const [formData, setFormData] = useState({
    requester_name: '',
    requester_email: '',
    note: '',
    tip: ''
  });
  const [paymentData, setPaymentData] = useState(null);

  const { submitting, submitRequest } = useSongRequests(artistId, mode, true, sessionId);
  const { toast } = useToast();
  
  // ✅ Use artist's currency if provided, otherwise fall back to visitor's currency from context
  let currencyContext = null;
  try {
    currencyContext = useCurrency();
  } catch (error) {
    // Context not available, use fallback
    console.warn('CurrencyContext not available, using fallback format:', error);
  }
  
  // Use artist's currency if provided, otherwise use visitor's currency from context, or default to GBP
  const currency = artistCurrency || currencyContext?.currency || DEFAULT_CURRENCY;
  const format = artistCurrency
    ? (amount) => formatCurrency(amount, artistCurrency)
    : (currencyContext?.format || ((amount) => formatCurrency(amount, currency)));

  useEffect(() => {
    if (!modalOpen) {
      viewedTrackedForOpenRef.current = false;
      cartTrackedForOpenRef.current = false;
    }
  }, [modalOpen]);

  // Track song view when request modal opens (resolve session_id if live_status lagged)
  useEffect(() => {
    if (!modalOpen || !artistId || !song?.id || viewedTrackedForOpenRef.current) return;
    viewedTrackedForOpenRef.current = true;
    let cancelled = false;
    (async () => {
      let sid = sessionId;
      if (!sid && isLive) sid = await resolveArtistLiveSessionId(artistId);
      if (cancelled) return;
      trackSongInteraction(artistId, song.id, 'viewed', sid);
    })();
    return () => {
      cancelled = true;
    };
  }, [modalOpen, artistId, song?.id, sessionId, isLive]);

  // "Added to cart" = user opened the live request flow for this song (matches funnel intent; avoids null session_id)
  useEffect(() => {
    if (!modalOpen || !artistId || !song?.id || !isLive || cartTrackedForOpenRef.current) return;
    cartTrackedForOpenRef.current = true;
    let cancelled = false;
    (async () => {
      let sid = sessionId ?? (await resolveArtistLiveSessionId(artistId));
      if (cancelled) return;
      trackSongInteraction(artistId, song.id, 'added_to_cart', sid);
    })();
    return () => {
      cancelled = true;
    };
  }, [modalOpen, artistId, song?.id, sessionId, isLive]);

  useEffect(() => {
    const onCloseAll = () => {
      setModalOpen(false);
      setShowPaymentModal(false);
    };
    window.addEventListener('ysrCloseSongRequestModals', onCloseAll);
    return () => window.removeEventListener('ysrCloseSongRequestModals', onCloseAll);
  }, []);

  // Prevent body scrolling when modal is open
  useEffect(() => {
    if (modalOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [modalOpen]);

  const getThemeStyles = () => {
    switch (theme) {
      case 'karaoke':
        return {
          card: 'bg-purple-900/50 border-purple-600 hover:border-purple-500 hover:bg-purple-800/50',
          title: 'text-purple-300',
          artist: 'text-gray-400',
          price: 'text-green-400',
          button: 'text-purple-400 hover:text-purple-300 hover:bg-purple-800/20 bg-purple-900/10',
        };
      case 'artist':
      default:
        return {
          card: 'bg-gray-900/50 border-gray-600 hover:border-gray-500 hover:bg-gray-800/50',
          title: 'text-blue-300',
          artist: 'text-gray-400',
          price: 'text-green-400',
          button: 'text-blue-400 hover:text-blue-300 hover:bg-blue-900/20 bg-blue-900/10',
        };
    }
  };

  const styles = getThemeStyles();

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.requester_name) {
      return false;
    }

        // If payment is required (song has price), show payment modal
        // Close the request modal first: its overlay is z-[100000] but Dialog is z-[99999],
        // so payment would render behind and taps would appear to do nothing.
        if (mode === 'artist' && Number(song.price) > 0) {
          // Store pending payment in localStorage to track "in basket" status
          const pendingPaymentKey = `pending_payment_${artistId}_${song.id}`;
          const pendingPaymentData = {
            songId: song.id,
            songTitle: song.title,
            artistId: artistId,
            timestamp: Date.now(),
            formData: formData
          };
          localStorage.setItem(pendingPaymentKey, JSON.stringify(pendingPaymentData));
          sessionStorage.setItem(pendingPaymentKey, JSON.stringify(pendingPaymentData));
          
          setModalOpen(false);
          setShowPaymentModal(true);
          return false; // Don't submit yet
        }

    // Otherwise, submit directly
    await submitRequestWithData();
    return true;
  };

  const handlePaymentSuccess = async (paymentResult) => {
    console.log('✅ Payment success callback called with:', paymentResult);
    setPaymentData(paymentResult);
    setShowPaymentModal(false);
    setModalOpen(false);
    
    // Track paid interaction
    if (artistId && song?.id) {
      (async () => {
        let sid = sessionId;
        if (!sid && isLive) sid = await resolveArtistLiveSessionId(artistId);
        trackSongInteraction(artistId, song.id, 'paid', sid);
      })();
    }
    
    // Clear pending payment from storage (request is completing)
    const pendingPaymentKey = `pending_payment_${artistId}_${song.id}`;
    localStorage.removeItem(pendingPaymentKey);
    sessionStorage.removeItem(pendingPaymentKey);
    
    // Trust Payments backend already creates the request for artist mode.
    // Avoid duplicate inserts from the client.
    setFormData({
      requester_name: '',
      requester_email: '',
      note: '',
      tip: ''
    });
    setPaymentData(null);

    if (onRequestSubmitted) {
      onRequestSubmitted();
    }

    if (isPayPalPaymentProvider(paymentResult?.paymentProvider)) {
      toast({
        title: 'Payment complete',
        description: 'Your payment went through PayPal. Your song request is in the queue.',
      });
    }
  };

  const handlePaymentCancel = () => {
    setShowPaymentModal(false);
    // Clear pending payment if user cancels
    const pendingPaymentKey = `pending_payment_${artistId}_${song.id}`;
    localStorage.removeItem(pendingPaymentKey);
    sessionStorage.removeItem(pendingPaymentKey);
    setModalOpen(true);
  };

  const submitRequestWithData = async (paymentResult = null) => {
    const requestData = {
      song_id: song.id,
      song_title: song.title,
      // For Artist Mode, use original_artist if available, otherwise use artist field, or fallback
      // For Karaoke Mode, original_artist is required (handled by validation in useSongRequests)
      original_artist: song.original_artist || song.artist || 'Unknown Artist',
      requester_name: formData.requester_name,
      requester_email: formData.requester_email,
      note: formData.note,
      tip: formData.tip,
      price: song.price,
      transaction_reference: paymentResult?.transactionReference,
      order_reference: paymentResult?.orderReference,
    };

    const success = await submitRequest(requestData);
    
    if (success) {
      // Clear pending payment on success
      const pendingPaymentKey = `pending_payment_${artistId}_${song.id}`;
      localStorage.removeItem(pendingPaymentKey);
      sessionStorage.removeItem(pendingPaymentKey);
      
      setModalOpen(false);
      setFormData({
        requester_name: '',
        requester_email: '',
        note: '',
        tip: ''
      });
      setPaymentData(null);
      
      if (onRequestSubmitted) {
        onRequestSubmitted();
      }
    }

    return success;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    await handleFormSubmit(e);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const byline = `by ${capitalizeWords(song.original_artist || song.artist || 'Unknown Artist')}`;
  const priceText = format(Number(song.price) || 0);

  // Don't show request button if song is already played or not live
  if (!isLive || isPlayed) {
    return (
      <div className={`flex justify-between items-center ${subtitlePriceInline ? 'p-3' : 'p-4'} rounded-lg transition-all duration-200 ${
        isPlayed 
          ? `${styles.card.replace('hover:', '')} opacity-60` 
          : styles.card
      }`}>
        <div className="flex-1 min-w-0">
          <div className="flex items-center space-x-2">
            <p className={`font-bold text-lg ${
              isPlayed ? styles.title.replace('300', '600') + ' line-through' : styles.title
            }`}>
              {capitalizeWords(song.title)}
            </p>
            {song.requestCount > 0 && (
              <div className="flex items-center space-x-1 text-xs text-gray-400">
                <User className="w-3 h-3" />
                <span>{song.requestCount}</span>
              </div>
            )}
          </div>
          {subtitlePriceInline ? (
            <p
              className={`mt-0.5 text-sm flex flex-wrap items-baseline gap-x-1.5 ${
                isPlayed ? styles.artist.replace('400', '700') : styles.artist
              }`}
            >
              <span>{byline}</span>
              <span className="text-white/35 font-normal select-none" aria-hidden>
                ·
              </span>
              <span
                className={`font-semibold tabular-nums text-sm ${
                  isPlayed ? styles.price.replace('400', '600') : styles.price
                }`}
              >
                {priceText}
              </span>
            </p>
          ) : (
            <p className={`text-sm ${
              isPlayed ? styles.artist.replace('400', '700') : styles.artist
            }`}>
              {byline}
            </p>
          )}
        </div>
        
        <div className={`text-right shrink-0 flex flex-col items-end justify-center ${subtitlePriceInline ? 'ml-3' : 'ml-4'}`}>
          {!subtitlePriceInline && (
            <p className={`font-semibold text-xl ${
              isPlayed ? styles.price.replace('400', '600') : styles.price
            }`}>
              {priceText}
            </p>
          )}
          
          {isPlayed ? (
            <span className={`${styles.title.replace('300', '600')} font-medium ${subtitlePriceInline ? 'mt-1' : 'mt-2'} block`}>
              Already Played
            </span>
          ) : (
            <span className={`${styles.artist.replace('400', '500')} font-medium ${subtitlePriceInline ? 'mt-1' : 'mt-2'} block`}>
              Offline
            </span>
          )}
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Song Card */}
      <div className={`flex justify-between items-center ${subtitlePriceInline ? 'p-3' : 'p-4'} rounded-lg transition-all duration-200 ${styles.card} cursor-pointer group ${inBasket ? 'ring-1 ring-violet-400/55' : ''}`}>
        <div className="flex-1 min-w-0">
          <div className="flex items-center space-x-2">
            <p className={`font-bold text-lg ${styles.title} group-hover:${styles.title.replace('300', '200')} transition-colors`}>
              {capitalizeWords(song.title)}
            </p>
            {song.requestCount > 0 && (
              <div className="flex items-center space-x-1 text-xs text-gray-400">
                <User className="w-3 h-3" />
                <span>{song.requestCount}</span>
              </div>
            )}
          </div>
          {subtitlePriceInline ? (
            <p
              className={`mt-0.5 text-sm flex flex-wrap items-baseline gap-x-1.5 ${styles.artist} group-hover:${styles.artist.replace('400', '300')} transition-colors`}
            >
              <span>{byline}</span>
              <span className="text-white/35 font-normal select-none" aria-hidden>
                ·
              </span>
              <span
                className={`font-semibold tabular-nums text-sm ${styles.price} group-hover:${styles.price.replace('400', '300')} transition-colors`}
              >
                {priceText}
              </span>
            </p>
          ) : (
            <p className={`text-sm ${styles.artist} group-hover:${styles.artist.replace('400', '300')} transition-colors`}>
              {byline}
            </p>
          )}
        </div>
        
        <div className={`text-right shrink-0 flex flex-col items-end justify-center ${subtitlePriceInline ? 'ml-3' : 'ml-4'}`}>
          {!subtitlePriceInline && (
            <p className={`font-semibold text-xl ${styles.price} group-hover:${styles.price.replace('400', '300')} transition-colors`}>
              {priceText}
            </p>
          )}
          
          <button
            onClick={() => {
              if (basketMode && typeof onToggleBasket === 'function') {
                if (!acceptingNewRequests && !inBasket) return;
                onToggleBasket(song);
                return;
              }
              // Check if there's a pending payment for this song (in basket)
              const pendingPaymentKey = `pending_payment_${artistId}_${song.id}`;
              const pendingPayment = localStorage.getItem(pendingPaymentKey) || sessionStorage.getItem(pendingPaymentKey);
              
              // Allow if requests are accepted OR if there's a pending payment (in basket)
              if (acceptingNewRequests || pendingPayment) {
                setModalOpen(true);
              }
            }}
            disabled={
              basketMode
                ? !acceptingNewRequests && !inBasket
                : !acceptingNewRequests && !(localStorage.getItem(`pending_payment_${artistId}_${song.id}`) || sessionStorage.getItem(`pending_payment_${artistId}_${song.id}`))
            }
            className={`inline-flex items-center justify-center px-4 py-2 rounded-xl font-medium transition-all duration-200 ${
              inBasket
                ? 'bg-violet-600/25 text-violet-200 border-2 border-violet-400/50 hover:bg-violet-600/35 hover:scale-105 cursor-pointer'
                : acceptingNewRequests 
                ? `${styles.button} border-2 border-current/30 hover:border-current/50 hover:scale-105 cursor-pointer` 
                : 'bg-gray-700/50 text-gray-500 border-2 border-gray-600/30 cursor-not-allowed opacity-60'
            } ${subtitlePriceInline ? 'mt-0 py-1.5 text-sm' : 'mt-2'}`}
          >
            {inBasket ? 'In basket' : 'Request Song'}
          </button>
        </div>
      </div>

      {/* Modal Overlay */}
      {createPortal((
        <AnimatePresence>
          {modalOpen && (
        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2, ease: 'easeOut' }}
          className="fixed inset-0 bg-black/85 backdrop-blur-sm z-[100000] overflow-y-auto overscroll-contain px-4 py-4"
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 100000,
            WebkitOverflowScrolling: 'touch'
          }}
          onClick={() => setModalOpen(false)}
        >
          {/* Modal Content */}
          <motion.div 
            initial={{ y: 80, opacity: 0, scale: 0.98 }}
            animate={{ y: 0, opacity: 1, scale: 1 }}
            exit={{ y: 80, opacity: 0, scale: 0.98 }}
            transition={{ type: 'spring', damping: 26, stiffness: 280 }}
            className="bg-gray-900 border border-gray-600 rounded-2xl p-6 w-full max-w-sm sm:max-w-md mx-auto"
            style={{
              position: 'relative',
              zIndex: 100001,
              overflowX: 'hidden',
              minHeight: 'fit-content',
              paddingBottom: '80px' // Extra padding to ensure button is accessible
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close Button */}
            <button 
              onClick={() => setModalOpen(false)}
              className="absolute right-4 top-4 w-8 h-8 bg-gray-800 hover:bg-gray-700 text-gray-300 hover:text-white rounded-lg flex items-center justify-center transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
            
            {/* Header */}
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-10 h-10 bg-blue-600 rounded-xl flex items-center justify-center">
                <Music className="w-5 h-5 text-white" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-white">Request "{capitalizeWords(song.title)}"</h2>
                <p className="text-blue-200 text-sm">
                  Your request will be sent to the {mode === 'karaoke' ? 'karaoke host' : 'artist'} in real-time.
                </p>
              </div>
            </div>
            
            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor={`name-${song.id}`} className="text-sm font-medium text-gray-200">Your Name</Label>
                <Input 
                  id={`name-${song.id}`} 
                  name="requester_name"
                  value={formData.requester_name} 
                  onChange={handleInputChange} 
                  required 
                  className="mt-1 bg-gray-800 border-gray-600 text-white text-base" 
                  style={{ fontSize: '16px' }}
                />
              </div>
              
              {mode === 'artist' && (
                <div>
                  <Label htmlFor={`email-${song.id}`} className="text-sm font-medium text-gray-200">Email (optional)</Label>
                  <Input 
                    id={`email-${song.id}`} 
                    name="requester_email"
                    type="email" 
                    value={formData.requester_email} 
                    onChange={handleInputChange} 
                    className="mt-1 bg-gray-800 border-gray-600 text-white text-base" 
                    style={{ fontSize: '16px' }}
                  />
                </div>
              )}
              
              <div>
                <Label htmlFor={`note-${song.id}`} className="text-sm font-medium text-gray-200">
                  Note (optional)
                </Label>
                <Textarea 
                  id={`note-${song.id}`} 
                  name="note"
                  value={formData.note} 
                  onChange={handleInputChange} 
                  className="mt-1 bg-gray-800 border-gray-600 text-white text-base resize-none overflow-y-auto" 
                  style={{ 
                    fontSize: '16px',
                    minHeight: '40px',
                    maxHeight: '120px',
                    height: '40px'
                  }}
                  rows={1}
                  onInput={(e) => {
                    e.target.style.height = '40px';
                    e.target.style.height = `${Math.min(e.target.scrollHeight, 120)}px`;
                  }}
                />
              </div>
              
              {mode === 'artist' && (
                <div>
                  <Label htmlFor={`tip-${song.id}`} className="text-sm font-medium text-gray-200">Tip (optional)</Label>
                  <Input 
                    id={`tip-${song.id}`} 
                    name="tip"
                    type="number" 
                    step="0.01" 
                    value={formData.tip} 
                    onChange={handleInputChange} 
                    placeholder="e.g., 5.00" 
                    className="mt-1 bg-gray-800 border-gray-600 text-white text-base" 
                    style={{ fontSize: '16px' }}
                  />
                </div>
              )}
              
              {mode === 'artist' && (
                <div className="bg-blue-500/20 border border-blue-500/30 rounded-xl p-4 text-center">
                  <p className="text-sm text-blue-200 mb-1">Total Amount</p>
                  <p className="text-2xl font-bold text-white">
                    {format(Number(song.price) + (parseFloat(formData.tip) || 0))}
                  </p>
                </div>
              )}
              
              <Button 
                type="submit" 
                disabled={submitting} 
                className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-6 rounded-xl disabled:opacity-50 sticky bottom-0 mt-4 shadow-lg"
                style={{
                  position: 'sticky',
                  bottom: 0,
                  zIndex: 10,
                  backgroundColor: submitting ? 'rgba(37, 99, 235, 0.5)' : 'rgb(37, 99, 235)'
                }}
              >
                <Send className="w-4 h-4 mr-2" /> 
                {submitting ? 'Sending...' : (mode === 'artist' && song.price > 0 ? 'Continue to Payment' : 'Send Request')}
              </Button>
            </form>
          </motion.div>
        </motion.div>
          )}
        </AnimatePresence>
      ), document.body)}

      {/* Payment Modal */}
      {showPaymentModal && mode === 'artist' && song.price > 0 && (
        <Dialog open={showPaymentModal} onOpenChange={setShowPaymentModal}>
          <DialogContent className="bg-gray-900 border-gray-600 text-white max-w-md mx-auto max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center">
                <CreditCard className="w-5 h-5 mr-2" />
                {usePayPal ? 'Pay with PayPal' : 'Complete Payment'}
              </DialogTitle>
              <DialogDescription>
                {usePayPal ? (
                  <>
                    {format(Number(song.price) + (parseFloat(formData.tip) || 0))} — complete
                    checkout in the secure PayPal window for &quot;
                    {capitalizeWords(song.title)}&quot;
                  </>
                ) : (
                  <>
                    Pay {format(Number(song.price) + (parseFloat(formData.tip) || 0))} to request &quot;
                    {capitalizeWords(song.title)}&quot;
                  </>
                )}
              </DialogDescription>
            </DialogHeader>
            
            {usePayPal ? (
              <Suspense
                fallback={
                  <div className="flex items-center justify-center gap-2 py-8 text-gray-400">
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Loading checkout…
                  </div>
                }
              >
                <PayPalUSProcessor
                  artistCountry={artistCountry}
                  artistId={artistId}
                  amount={Number(song.price) + (parseFloat(formData.tip) || 0)}
                  description={`Song request: ${capitalizeWords(song.title)} by ${capitalizeWords(song.original_artist || song.artist || 'Unknown Artist')}${formData.tip ? ` + ${format(parseFloat(formData.tip) || 0)} tip` : ''}`}
                  onSuccess={handlePaymentSuccess}
                  onCancel={handlePaymentCancel}
                  mode="artist"
                  requestData={{
                    songId: song.id,
                    songTitle: song.title,
                    originalArtist: song.original_artist || song.artist || 'Unknown Artist',
                    requesterName: formData.requester_name || 'Anonymous',
                    requesterEmail: formData.requester_email || null,
                    note: formData.note || '',
                    sessionId: sessionId || null,
                    tipAmount: parseFloat(formData.tip) || 0,
                  }}
                />
              </Suspense>
            ) : (
              <A2BPaymentForm
                amount={Number(song.price) + (parseFloat(formData.tip) || 0)}
                artistId={artistId}
                description={`Song request: ${capitalizeWords(song.title)} by ${capitalizeWords(song.original_artist || song.artist || 'Unknown Artist')}${formData.tip ? ` + ${format(parseFloat(formData.tip) || 0)} tip` : ''}`}
                onSuccess={handlePaymentSuccess}
                onCancel={handlePaymentCancel}
                mode="artist"
                currency={artistCurrency || undefined}
                requestData={{
                  songId: song.id,
                  songTitle: song.title,
                  originalArtist: song.original_artist || song.artist || 'Unknown Artist',
                  requesterName: formData.requester_name || 'Anonymous',
                  requesterEmail: formData.requester_email || null,
                  note: formData.note || '',
                  sessionId: sessionId || null,
                  tipAmount: parseFloat(formData.tip) || 0,
                }}
              />
            )}
          </DialogContent>
        </Dialog>
      )}
    </>
  );
};




